import * as React from 'react';
import {
  AppBar, Box, Toolbar, IconButton, Typography, MenuItem, Menu, useMediaQuery, Avatar, Divider, Button, Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import logo from '../assets/LOGO2.png';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFilters } from '../context/FilterContext';

const getFormattedRole = (role) => {
  switch (role) {
    case 'mentor': return 'Mentor';
    case 'practice_head': return 'Practice Head';
    case 'coe_admin': return 'COE Admin';
    case 'super_admin': return 'Super Admin';
    case 'coe_report_admin': return 'COE Report Admin';
    case 'coe_report_viewer': return 'COE Report Viewer';
    default: return role;
  }
};

export default function Header({ handleDrawer }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [user, setUser] = React.useState({});
  const [learnerName, setLearnerName] = React.useState('');
  const isMenuOpen = Boolean(anchorEl);
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const location = useLocation();

  const menuId = 'primary-search-account-menu';

  const isLearnerReviewPage =
    /^\/mentor\/review\/learner\/[a-zA-Z0-9-]+$/.test(location.pathname) ||
    location.pathname === "/mentor/review/learner/topic-review";

  const isAdminRole = ['coe_admin', 'coe_report_admin', 'coe_report_viewer', 'super_admin', 'practice_head'].includes(user?.role);
  const titlePrefix = isAdminRole ? 'EJET Core.AI' : 'EJET Track.AI';

  const getCoeDisplayName = () => {
    if (user?.coeName) return user.coeName;
    if (user?.coe_name) return user.coe_name;
    const pathSegment = location.pathname.split('/')[1];
    if (pathSegment && !['admin', 'mentor', 'college-viewer'].includes(pathSegment)) {
      return pathSegment.replace(/-/g, ' ');
    }
    return 'COE';
  };

  const getHeaderTitle = () => {
    if (isLearnerReviewPage) return `${learnerName} Review`;
    if (user?.role === 'coe_report_viewer') {
      const coeDisplayName = getCoeDisplayName();
      return `${titlePrefix} - ${coeDisplayName} Report Dashboard`;
    }
    return `${titlePrefix} - ${getFormattedRole(user?.role)}`;
  };

  React.useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    setUser(userData || {});
    setLearnerName(localStorage.getItem('learnerName') || '');

    if (userData?.role) {
      if (userData.role === 'coe_report_viewer') {
        const coeDisplayName = userData.coeName || userData.coe_name || 'COE';
        document.title = `${titlePrefix} - ${coeDisplayName} Report Dashboard`;
      } else {
        const isUserAdminRole = ['coe_admin', 'coe_report_admin', 'super_admin', 'practice_head'].includes(userData.role);
        document.title = isUserAdminRole ? 'EJET Core.AI' : 'EJET Track.AI';
      }
    } else {
      document.title = 'EJET Track.AI';
    }
  }, [location.pathname, titlePrefix]);

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const { clearAllFilters } = useFilters();

  const handleLogout = () => {
    const showMobileBackButtons = !['coe_admin', 'coe_report_admin', 'coe_report_viewer', 'super_admin'].includes(user?.role);
    const isViewer = user?.role === 'coe_report_viewer';

    localStorage.clear();
    sessionStorage.clear();
    clearAllFilters();
    toast.success("Logged out successfully");
    if (showMobileBackButtons) {
      // navigate("/");
      window.location.href = "/";
    } else if (isViewer) {
      const coeSegment = location.pathname.split('/')[1];
      if (coeSegment && !['admin', 'mentor', 'college-viewer'].includes(coeSegment)) {
        window.location.href = `/${coeSegment}`;
      } else {
        window.location.href = "/college-viewer";
      }
    } else {
      // navigate("/admin");
      window.location.href = "/admin";
    }
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 0,
        sx: {
          width: 320,
          maxWidth: '90vw',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          mt: 1.5,
          border: '1px solid #e9ecef',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden',
          '& .MuiList-root': {
            padding: 0,
          },
        },
      }}
    >
      {/* Profile Header */}
      <Box sx={{ 
        padding: '24px 20px 18px', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        textAlign: 'center',
        background: 'linear-gradient(180deg, #f8faff 0%, #ffffff 100%)',
        borderBottom: '1px solid #f0f2f5'
      }}>
        <Avatar
          sx={{
            width: 62,
            height: 62,
            background: 'linear-gradient(135deg, #2b7cf8 0%, #0d6efd 60%, #0856cb 100%)',
            color: '#ffffff',
            fontSize: '24px',
            fontWeight: 700,
            boxShadow: '0 8px 18px rgba(13, 110, 253, 0.28)',
            mb: 1.5
          }}
        >
          {user?.name ? user.name.charAt(0).toUpperCase() : <PersonOutlineIcon sx={{ fontSize: 32 }} />}
        </Avatar>

        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529', mb: 0.5 }}>
          {user?.name ? `Hi, ${user.name}!` : 'Welcome!'}
        </Typography>

        <Chip
          label={getFormattedRole(user?.role) || 'User'}
          size="small"
          sx={{
            height: 24,
            fontSize: '11px',
            fontWeight: 700,
            color: '#0d6efd',
            backgroundColor: 'rgba(13, 110, 253, 0.08)',
            border: '1px solid rgba(13, 110, 253, 0.16)',
            borderRadius: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            mt: 0.5
          }}
        />
      </Box>

      {/* User Details Details Box */}
      <Box sx={{ padding: '16px 20px' }}>
        <Box sx={{
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          border: '1px solid #edf0f2',
          padding: '12px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}>
          {/* Email */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <EmailOutlinedIcon sx={{ fontSize: 18, color: '#6c757d', mt: '2px', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '11px', color: '#8c98a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.2 }}>
                Email
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#212529', fontWeight: 500, wordBreak: 'break-all', mt: '2px' }}>
                {user?.email || '--'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#e9ecef' }} />

          {/* Mobile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhoneOutlinedIcon sx={{ fontSize: 18, color: '#6c757d', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '11px', color: '#8c98a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.2 }}>
                Mobile
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#212529', fontWeight: 500, mt: '2px' }}>
                {user?.phoneNumber || '--'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      {/* Logout Action */}
      <Box sx={{ padding: '14px 20px 18px' }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleLogout}
          startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13.5px',
            color: '#dc3545',
            backgroundColor: '#fff5f5',
            border: '1px solid #ffd8d8',
            borderRadius: '10px',
            py: 1,
            boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#dc3545',
              color: '#ffffff',
              borderColor: '#dc3545',
              boxShadow: '0 4px 12px rgba(220, 53, 69, 0.25)'
            }
          }}
        >
          Sign Out
        </Button>
      </Box>
    </Menu>
  );

  const showMobileBackButtons = isMobile && !['coe_admin', 'coe_report_admin', 'coe_report_viewer', 'super_admin'].includes(user?.role);

  return (
    <Box sx={{ flexGrow: 1, position: 'sticky', top: 0, zIndex: 10, width: '100%' }}>
      <AppBar position="static">
        <Toolbar sx={{ backgroundColor: 'white', color: 'black' }}>
          {showMobileBackButtons ? (
            <>
              <IconButton onClick={() => navigate(-1)} color="inherit">
                <ArrowBackIcon sx={{ fontSize: 30 }} />
              </IconButton>
              <IconButton onClick={() => navigate("/mentor/lab-practice")} color="inherit">
                <HomeIcon sx={{ fontSize: 30 }} />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={handleDrawer} color="inherit" sx={{ ml: { xs: -1.5, sm: -2 } }}>
              <MenuIcon sx={{ fontSize: 28 }} />
            </IconButton>
          )}

          <Box sx={{ display: { xs: 'none', sm: 'block' }, ml: 2 }}>
            <img src={logo} alt="Logo" />
          </Box>

          <Typography noWrap sx={{ m: 2, fontWeight: 600, fontSize: 16 }}>
            {getHeaderTitle()}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            edge="end"
            aria-controls={menuId}
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <AccountCircle sx={{ fontSize: 35 }} />
          </IconButton>
        </Toolbar>
      </AppBar>
      {renderMenu}
    </Box>
  );
}
